import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './ConversationPage.css';
import { backendUrl } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModuleData } from '../hooks/useModuleData';
import { useWordGroupData } from '../hooks/useWordGroupData';
import { useWordsData } from '../hooks/useWordsData';

const ConversationPage = () => {
  const { id, groupId } = useParams();
  const { moduleData: mod, loading: moduleLoading } = useModuleData(id);
  const { groupData: group, loading: groupLoading } = useWordGroupData(id, groupId);
  const { words, loading: wordsLoading } = useWordsData(group?.id);
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatWindowRef = useRef(null);
  const { t, isRTL } = useLanguage();
  const requestIdRef = useRef(0);
  const [error, setError] = useState('');

  const handleInitialMessage = useCallback(async () => {
    setLoading(true);
    setError('');
    const currentRequestId = ++requestIdRef.current;
    try {
      const res = await fetch(`${backendUrl}/gemini-conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: group.name,
          fromLang: mod.from_lang,
          toLang: mod.to_lang,
          words: words || [],
        }),
      });
      const data = await res.json();
      if (requestIdRef.current === currentRequestId) {
        if (data.error) {
          setError(data.error);
        } else {
          setHistory([{ role: 'ai', text: data.reply }]);
        }
      }
    } catch (e) {
      if (requestIdRef.current === currentRequestId) {
        setError(t('couldNotConnectToAI'));
        console.error(e);
      }
    }
    if (requestIdRef.current === currentRequestId) setLoading(false);
  }, [group, mod?.from_lang, mod?.to_lang, words, t]);

  useEffect(() => {
    // Scroll to bottom of chat window when history changes
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [history, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;
    const message = userInput.trim();
    setUserInput('');
    setLoading(true);
    const currentRequestId = ++requestIdRef.current;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/gemini-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message, 
          fromLang: mod.from_lang, 
          toLang: mod.to_lang,
          wordGroup: words || [] 
        })
      });
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      const data = await response.json();
      if (requestIdRef.current === currentRequestId) {
        setHistory(prev => [...prev, { role: 'user', content: message }, { role: 'assistant', content: data.response }]);
      }
    } catch (error) {
      if (requestIdRef.current === currentRequestId) {
        console.error('Error sending message:', error);
      }
    } finally {
      if (requestIdRef.current === currentRequestId) setLoading(false);
    }
  };

  const handleStartConversation = async () => {
    setLoading(true);
    const currentRequestId = ++requestIdRef.current;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/gemini-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: 'Start a conversation to help me practice these words', 
          fromLang: mod.from_lang, 
          toLang: mod.to_lang,
          wordGroup: words || [] 
        })
      });
      if (!response.ok) {
        throw new Error('Failed to start conversation');
      }
      const data = await response.json();
      if (requestIdRef.current === currentRequestId) {
        setHistory([{ role: 'assistant', content: data.response }]);
      }
    } catch (error) {
      if (requestIdRef.current === currentRequestId) {
        console.error('Error starting conversation:', error);
      }
    } finally {
      if (requestIdRef.current === currentRequestId) setLoading(false);
    }
  };

  useEffect(() => {
    if (group && mod && words) {
      handleInitialMessage();
    }
  }, [group, mod, words, handleInitialMessage]);

  if (moduleLoading || groupLoading || wordsLoading) {
    return (
      <div className="card" dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: 'center', padding: '2rem' }}>
        <p>{t('loading') || 'Loading...'}</p>
      </div>
    );
  }

  if (!mod || !group) {
    return (
      <div className="card" dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('wordGroupNotFound')}</h2>
        <p>{t('wordGroupNotFoundMessage')}</p>
        <Link to="/" className="btn btn-primary">{t('backToModules')}</Link>
      </div>
    );
  }

  return (
    <div className="conversation-page-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="conversation-header">
        <button onClick={() => navigate(`/module/${mod.id}/group/${group.id}`)} className="btn btn-secondary btn-back">
          {t('backToWordGroup')}
        </button>
        <h2>{t('startConversation')}</h2>
      </div>

      <div className="conversation-container">
        <div className="conversation-messages" ref={chatWindowRef}>
          {error && (
            <div className="error-message">{error}</div>
          )}
          {history.length === 0 && !error ? (
            <div className="conversation-start">
              <p>{t('startConversation')}</p>
              <button onClick={handleStartConversation} className="btn btn-primary" disabled={loading}>
                {t('startConversation')}
              </button>
            </div>
          ) : (
            history.map((msg, index) => (
              <div key={index} className={`conversation-message ${msg.role}`}>
                <div className="message-content">{msg.content}</div>
              </div>
            ))
          )}
          {loading && <div className="typing-indicator"><em>{t('aiTyping')}</em></div>}
        </div>

        <form onSubmit={handleSendMessage} className="message-form">
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder={t('typeMessage')}
            disabled={loading}
            className="message-input"
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>{t('send')}</button>
        </form>
      </div>
    </div>
  );
};

export default ConversationPage; 